import { Idea, Message, Attachment } from '../types';
import { logger } from './loggingService';

const DB_NAME = 'ConceptCanvasDB';
const DB_VERSION = 3;

const STORES = {
  IDEAS: 'ideas',
  MESSAGES: 'messages',
  ATTACHMENTS: 'attachments',
  METADATA: 'metadata',
  HANDLES: 'handles'
} as const;

export const GUEST_ACCOUNT_ID = 'guest';

interface StoredAttachment {
  id: string;
  ideaId: string;
  name: string;
  type: 'image' | 'document';
  mimeType: string;
  timestamp: number;
  blob?: Blob;
  data?: string;
}

interface SyncMetadata {
  lastLocalUpdate: number;
  lastDriveSync: number;
  pendingUpload: boolean;
  version: number;
}

const DEFAULT_METADATA: SyncMetadata = {
  lastLocalUpdate: 0,
  lastDriveSync: 0,
  pendingUpload: false,
  version: 1
};

class OfflineStorageService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const oldVersion = event.oldVersion;

        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains(STORES.HANDLES)) {
            db.createObjectStore(STORES.HANDLES);
          }
        }

        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains(STORES.IDEAS)) {
            const ideaStore = db.createObjectStore(STORES.IDEAS, { keyPath: 'id' });
            ideaStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          }

          if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
            const msgStore = db.createObjectStore(STORES.MESSAGES, { keyPath: 'id' });
            msgStore.createIndex('ideaId', 'ideaId', { unique: false });
            msgStore.createIndex('timestamp', 'timestamp', { unique: false });
          }

          if (!db.objectStoreNames.contains(STORES.ATTACHMENTS)) {
            const attachStore = db.createObjectStore(STORES.ATTACHMENTS, { keyPath: 'id' });
            attachStore.createIndex('ideaId', 'ideaId', { unique: false });
          }

          if (!db.objectStoreNames.contains(STORES.METADATA)) {
            db.createObjectStore(STORES.METADATA);
          }
        }

        if (oldVersion < 3) {
          const tx = (event.target as IDBOpenDBRequest).transaction;
          if (tx) {
            const ideasStore = tx.objectStore(STORES.IDEAS);
            const messagesStore = tx.objectStore(STORES.MESSAGES);

            if (ideasStore && !ideasStore.indexNames.contains('accountId')) {
              ideasStore.createIndex('accountId', 'accountId', { unique: false });
            }
            if (messagesStore && !messagesStore.indexNames.contains('accountId')) {
              messagesStore.createIndex('accountId', 'accountId', { unique: false });
            }
          }
        }

        logger.info('System', `IndexedDB upgraded from v${oldVersion} to v${DB_VERSION}`);
      };

      request.onsuccess = () => {
        this.db = request.result;
        logger.info('System', 'IndexedDB initialized successfully');
        resolve();
      };

      request.onerror = () => {
        logger.error('System', 'IndexedDB initialization failed', { error: request.error });
        reject(request.error);
      };
    });

    return this.initPromise;
  }

  private async ensureDB(): Promise<IDBDatabase> {
    await this.init();
    if (!this.db) throw new Error('IndexedDB not available');
    return this.db;
  }

  async saveIdea(idea: Idea): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.IDEAS, 'readwrite');
      const store = tx.objectStore(STORES.IDEAS);

      const ideaToStore = { ...idea };
      if (ideaToStore.attachments) {
        ideaToStore.attachments = ideaToStore.attachments.map(a => ({
          ...a,
          data: a.data?.length > 10000 ? '[STORED_IN_IDB]' : a.data
        }));
      }

      store.put(ideaToStore);
      tx.oncomplete = () => {
        this.updateMetadata({ lastLocalUpdate: Date.now(), pendingUpload: true });
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  async getIdea(id: string): Promise<Idea | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.IDEAS, 'readonly');
      const req = tx.objectStore(STORES.IDEAS).get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  }

  async getAllIdeas(): Promise<Idea[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.IDEAS, 'readonly');
      const req = tx.objectStore(STORES.IDEAS).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  async getIdeasForAccount(accountId: string): Promise<Idea[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.IDEAS, 'readonly');
      const store = tx.objectStore(STORES.IDEAS);

      if (store.indexNames.contains('accountId')) {
        const index = store.index('accountId');
        const req = index.getAll(IDBKeyRange.only(accountId));
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      } else {
        const req = store.getAll();
        req.onsuccess = () => {
          const all = req.result || [];
          const filtered = all.filter(i => (i.accountId || GUEST_ACCOUNT_ID) === accountId);
          resolve(filtered);
        };
        req.onerror = () => reject(req.error);
      }
    });
  }

  async deleteIdea(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.IDEAS, STORES.MESSAGES, STORES.ATTACHMENTS], 'readwrite');

      tx.objectStore(STORES.IDEAS).delete(id);

      const msgIndex = tx.objectStore(STORES.MESSAGES).index('ideaId');
      const msgCursor = msgIndex.openCursor(IDBKeyRange.only(id));
      msgCursor.onsuccess = (e: any) => {
        const cursor = e.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      const attIndex = tx.objectStore(STORES.ATTACHMENTS).index('ideaId');
      const attCursor = attIndex.openCursor(IDBKeyRange.only(id));
      attCursor.onsuccess = (e: any) => {
        const cursor = e.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      tx.oncomplete = () => {
        this.updateMetadata({ lastLocalUpdate: Date.now(), pendingUpload: true });
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  }

  async saveMessage(message: Message): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.MESSAGES, 'readwrite');
      tx.objectStore(STORES.MESSAGES).put(message);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async deleteMessage(id: string): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.MESSAGES, 'readwrite');
      tx.objectStore(STORES.MESSAGES).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getMessagesForIdea(ideaId: string): Promise<Message[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.MESSAGES, 'readonly');
      const index = tx.objectStore(STORES.MESSAGES).index('ideaId');
      const req = index.getAll(IDBKeyRange.only(ideaId));
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  async getAllMessages(): Promise<Message[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.MESSAGES, 'readonly');
      const req = tx.objectStore(STORES.MESSAGES).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  async getMessagesForAccount(accountId: string): Promise<Message[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.MESSAGES, 'readonly');
      const store = tx.objectStore(STORES.MESSAGES);

      if (store.indexNames.contains('accountId')) {
        const index = store.index('accountId');
        const req = index.getAll(IDBKeyRange.only(accountId));
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
      } else {
        const req = store.getAll();
        req.onsuccess = () => {
          const all = req.result || [];
          const filtered = all.filter(m => (m.accountId || GUEST_ACCOUNT_ID) === accountId);
          resolve(filtered);
        };
        req.onerror = () => reject(req.error);
      }
    });
  }

  async saveAttachment(ideaId: string, attachment: Attachment, blob?: Blob): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.ATTACHMENTS, 'readwrite');
      const stored: StoredAttachment = {
        id: attachment.id,
        ideaId,
        name: attachment.name,
        type: attachment.type,
        mimeType: attachment.mimeType,
        timestamp: attachment.timestamp,
        blob: blob,
        data: blob ? undefined : attachment.data
      };
      tx.objectStore(STORES.ATTACHMENTS).put(stored);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getAttachment(id: string): Promise<StoredAttachment | null> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.ATTACHMENTS, 'readonly');
      const req = tx.objectStore(STORES.ATTACHMENTS).get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  }

  async getAttachmentsForIdea(ideaId: string): Promise<StoredAttachment[]> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.ATTACHMENTS, 'readonly');
      const index = tx.objectStore(STORES.ATTACHMENTS).index('ideaId');
      const req = index.getAll(IDBKeyRange.only(ideaId));
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  async getMetadata(): Promise<SyncMetadata> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.METADATA, 'readonly');
      const req = tx.objectStore(STORES.METADATA).get('sync');
      req.onsuccess = () => resolve(req.result || DEFAULT_METADATA);
      req.onerror = () => reject(req.error);
    });
  }

  async updateMetadata(updates: Partial<SyncMetadata>): Promise<void> {
    const db = await this.ensureDB();
    const current = await this.getMetadata();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.METADATA, 'readwrite');
      tx.objectStore(STORES.METADATA).put({ ...current, ...updates }, 'sync');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async exportAllData(): Promise<{ ideas: Idea[], messages: Message[], attachments: StoredAttachment[] }> {
    const [ideas, messages] = await Promise.all([
      this.getAllIdeas(),
      this.getAllMessages()
    ]);

    const attachments: StoredAttachment[] = [];
    for (const idea of ideas) {
      const ideaAttachments = await this.getAttachmentsForIdea(idea.id);
      attachments.push(...ideaAttachments);
    }

    return { ideas, messages, attachments };
  }

  async importData(data: { ideas: Idea[], messages: Message[], attachments?: StoredAttachment[] }): Promise<{ imported: number, skipped: number }> {
    let imported = 0;
    let skipped = 0;

    for (const idea of data.ideas) {
      try {
        await this.saveIdea(idea);
        imported++;
      } catch (e) {
        logger.warn('System', `Failed to import idea ${idea.id}`, { error: (e as any)?.message });
        skipped++;
      }
    }

    for (const message of data.messages) {
      try {
        await this.saveMessage(message);
      } catch (e) {
        logger.warn('System', `Failed to import message ${message.id}`);
      }
    }

    if (data.attachments) {
      for (const att of data.attachments) {
        try {
          const attachment: Attachment = {
            id: att.id,
            name: att.name,
            type: att.type,
            mimeType: att.mimeType,
            timestamp: att.timestamp,
            data: att.data || ''
          };
          await this.saveAttachment(att.ideaId, attachment, att.blob);
        } catch (e) {
          logger.warn('System', `Failed to import attachment ${att.id}`);
        }
      }
    }

    return { imported, skipped };
  }

  async clearAllData(): Promise<void> {
    const db = await this.ensureDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORES.IDEAS, STORES.MESSAGES, STORES.ATTACHMENTS, STORES.METADATA], 'readwrite');
      tx.objectStore(STORES.IDEAS).clear();
      tx.objectStore(STORES.MESSAGES).clear();
      tx.objectStore(STORES.ATTACHMENTS).clear();
      tx.objectStore(STORES.METADATA).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async migrateFromLocalStorage(): Promise<{ migrated: boolean, count: number }> {
    const LOCAL_STORAGE_KEY = 'cc_guest_data_v1';
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (!stored) return { migrated: false, count: 0 };

    try {
      const data = JSON.parse(stored);
      const ideas = data.ideas || [];
      const messages = data.messages || [];

      if (ideas.length === 0 && messages.length === 0) {
        return { migrated: false, count: 0 };
      }

      const existingIdeas = await this.getAllIdeas();
      if (existingIdeas.length > 0) {
        logger.info('System', 'IndexedDB already has data, skipping migration');
        return { migrated: false, count: 0 };
      }

      const result = await this.importData({ ideas, messages });
      logger.info('System', `Migrated ${result.imported} ideas from localStorage to IndexedDB`);

      return { migrated: true, count: result.imported };
    } catch (e) {
      logger.error('System', 'Failed to migrate from localStorage', { error: (e as any)?.message });
      return { migrated: false, count: 0 };
    }
  }

  async migrateToAccountNamespace(): Promise<{ migrated: number }> {
    const db = await this.ensureDB();
    let migrated = 0;

    const ideas = await this.getAllIdeas();
    for (const idea of ideas) {
      if (!idea.accountId) {
        idea.accountId = GUEST_ACCOUNT_ID;
        await this.saveIdea(idea);
        migrated++;
      }
    }

    const messages = await this.getAllMessages();
    for (const message of messages) {
      if (!message.accountId) {
        message.accountId = GUEST_ACCOUNT_ID;
        await this.saveMessage(message);
      }
    }

    if (migrated > 0) {
      logger.info('System', `Migrated ${migrated} ideas to account namespace`);
    }

    return { migrated };
  }

  async promoteIdeasToAccount(ideaIds: string[], fromAccountId: string, toAccountId: string): Promise<number> {
    let promoted = 0;

    for (const ideaId of ideaIds) {
      const idea = await this.getIdea(ideaId);
      if (idea && (idea.accountId || GUEST_ACCOUNT_ID) === fromAccountId) {
        idea.accountId = toAccountId;
        await this.saveIdea(idea);
        promoted++;

        const messages = await this.getMessagesForIdea(ideaId);
        for (const message of messages) {
          message.accountId = toAccountId;
          await this.saveMessage(message);
        }
      }
    }

    logger.info('System', `Promoted ${promoted} ideas from ${fromAccountId} to ${toAccountId}`);
    return promoted;
  }

  async getAccountIds(): Promise<string[]> {
    const ideas = await this.getAllIdeas();
    const accountIds = new Set<string>();
    for (const idea of ideas) {
      accountIds.add(idea.accountId || GUEST_ACCOUNT_ID);
    }
    return Array.from(accountIds);
  }
}

export const offlineStorage = new OfflineStorageService();
